import { NetworkErrorWhiteList } from 'constants/networkErrorWhiteList';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useLazyQuery, ApolloError } from '@apollo/client';
import { PlayerSource } from '@landr/react-player';
import { StreamEntityType } from 'types/enums';
import { StreamUrl } from 'types/generated/samplesApi';
import { StreamResponses, StreamQueries, StreamQueryVariables } from '../types';
import { OnError } from './useError';

type StreamQueryResponse = {
    [key: string]: StreamUrl | null;
};

const useStream = ({
    id,
    streamEntityType,
    getIsExpired,
    onError,
    seek,
    setPlaying,
    setSources,
    updateExpirationDate,
    setLoading,
    customStreamUrl,
}: {
    id?: string;
    streamEntityType: StreamEntityType;
    getIsExpired: () => boolean;
    onError: OnError;
    seek: (value: number, playOnSeek?: boolean | undefined) => void;
    setPlaying: (value: boolean) => void;
    setSources: (value: PlayerSource[]) => void;
    updateExpirationDate: () => void;
    setLoading: (value: boolean) => void;
    customStreamUrl?: string;
}) => {
    const [canUpdateSources, setCanUpdateSources] = useState(false);
    const lazySources = useRef<PlayerSource[]>([]);
    const canFetchStream =
        !!id &&
        !customStreamUrl &&
        streamEntityType !== StreamEntityType.Custom;

    const handleGetStreamCompleted = useCallback(
        (data: StreamQueryResponse) => {
            const streamUrl = data[StreamResponses[streamEntityType]]?.url;

            if (id && streamUrl) {
                const newSources = [{ id, url: streamUrl, preload: true }];

                if (canUpdateSources) {
                    setSources(newSources);
                } else {
                    lazySources.current = newSources;
                }
                updateExpirationDate();
            }
        },
        [
            canUpdateSources,
            setSources,
            streamEntityType,
            id,
            updateExpirationDate,
        ],
    );

    const handleGetStreamError = useCallback(
        (error: ApolloError, withNotifications = false) => {
            onError({
                error: error,
                errorMessage: `Failed to fetch a ${streamEntityType} stream URL`,
                extraArguments: {
                    id: id ?? 'undefined',
                },
                withNotifications: withNotifications,
            });
        },
        [onError, id, streamEntityType],
    );

    const [getStream] = useLazyQuery(StreamQueries[streamEntityType], {
        fetchPolicy: 'no-cache',
        onCompleted: handleGetStreamCompleted,
        onError: handleGetStreamError,
    });

    useEffect(() => {
        const abortController = new AbortController();

        canFetchStream &&
            getStream({
                variables: {
                    [StreamQueryVariables[streamEntityType]]: id,
                },
                context: {
                    fetchOptions: {
                        signal: abortController.signal,
                    },
                },
            });

        return () => {
            abortController.abort(NetworkErrorWhiteList.AbortController);
        };
    }, [getStream, streamEntityType, id, canFetchStream]);

    // Custom slide phase1
    useEffect(() => {
        !canFetchStream &&
            customStreamUrl &&
            setSources([
                { id: id || 'undefined', url: customStreamUrl, preload: false },
            ]);
    }, [canFetchStream, customStreamUrl, id, setSources]);

    const handlePlay = useCallback(
        (newTime?: number) =>
            Number.isFinite(newTime)
                ? seek(newTime as number, true)
                : setPlaying(true),
        [seek, setPlaying],
    );

    const handleGetStreamAndPlay = useCallback(
        async (newTime?: number) => {
            canFetchStream &&
                (await getStream({
                    variables: {
                        [StreamQueryVariables[streamEntityType]]: id,
                    },
                    onError: (error) => handleGetStreamError(error, true),
                }));
            handlePlay(newTime);
        },
        [
            canFetchStream,
            getStream,
            streamEntityType,
            id,
            handlePlay,
            handleGetStreamError,
        ],
    );

    const playStream = useCallback(
        (newTime?: number) => {
            if (!id) {
                onError({
                    errorMessage: `playStream was invoked with invalid value`,
                    error: new Error(
                        `playStream was invoked with invalid value`,
                    ),
                    extraArguments: {
                        id: id || 'undefined',
                    },
                });
                return;
            }

            const hasLazySources = lazySources.current.length > 0;

            if (!canUpdateSources) {
                setCanUpdateSources(true);
                hasLazySources && setSources(lazySources.current);
            }

            if ((!hasLazySources || getIsExpired()) && canFetchStream) {
                setLoading(true);
                handleGetStreamAndPlay(newTime);
            } else {
                handlePlay(newTime);
            }
        },
        [
            id,
            canUpdateSources,
            getIsExpired,
            canFetchStream,
            onError,
            setSources,
            setLoading,
            handleGetStreamAndPlay,
            handlePlay,
        ],
    );

    return {
        playStream,
    };
};

export default useStream;
